import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Container, Alert, Spinner, EmptyContent } from '@/components/ui';
import { withPermissions } from '@/components/withPermissions';
import { PERMISSIONS } from '@/types/permissions';
import { StudyDataImportWizard } from './components';
import { Navigate, useParams } from 'react-router-dom';
import { ImportWizardState, WizardStep } from './components/types';
import { ImportProgressPage } from './ImportProgressPage.tsx';
import { useAuth } from '@/contexts/AuthContext';
import {
  saveMappingState,
  loadMappingState,
  clearMappingState,
} from './utils/mappingStorage';
import { useStudyForms, clearStudyFormsCache } from './hooks/useStudyForms';
import { IconAlertTriangle } from '@tabler/icons-react';

const StudyDataImport: React.FC = () => {
  const params = useParams<{ step?: string; mappingId?: string }>();
  const { step: stepParam, mappingId } = params;
  const currentStep = stepParam as WizardStep | undefined;
  const { user: profile } = useAuth();
  const studyId = profile?.current_study?.id;
  const previousStudyIdRef = useRef<number | undefined>(studyId);

  // Load study forms centrally for the entire data-import section
  const {
    forms: studyForms,
    loading: loadingForms,
    error: formsError,
    hasForms,
  } = useStudyForms({
    enabled: !!studyId,
    autoLoad: true,
  });

  // Initialize wizard state from localStorage if available
  const getInitialState = useCallback((): ImportWizardState => {
    if (!studyId) {
      return {
        autoMappingEnabled: true,
        selectedFiles: [],
        fileToFormMappings: [],
        columnMappings: [],
        subformRelationships: [],
        validationResults: [],
        isValid: false,
        importMappingId: undefined,
      };
    }

    const savedState = loadMappingState(studyId);
    if (savedState) {
      return savedState;
    }

    return {
      autoMappingEnabled: true,
      selectedFiles: [],
      fileToFormMappings: [],
      columnMappings: [],
      subformRelationships: [],
      validationResults: [],
      isValid: false,
      importMappingId: undefined,
    };
  }, [studyId]);

  // Shared wizard state that persists across step changes
  const [wizardState, setWizardState] = useState<ImportWizardState>(
    getInitialState
  );

  const wizardStateRef = useRef(wizardState);
  useEffect(() => {
    wizardStateRef.current = wizardState;
  }, [wizardState]);

  // Load state from localStorage when study changes
  useEffect(() => {
    if (!studyId) return;

    // If study changed, clear old state and load new one
    if (
      previousStudyIdRef.current !== undefined &&
      previousStudyIdRef.current !== studyId
    ) {
      // Clear previous study's state from localStorage
      if (previousStudyIdRef.current) {
        clearMappingState(previousStudyIdRef.current);
      }
      // Clear study forms cache when study changes
      clearStudyFormsCache();
    }

    previousStudyIdRef.current = studyId;

    // Load state for current study
    const savedState = loadMappingState(studyId);
    if (savedState) {
      // Ensure availableForms is preserved or restored from studyForms if missing
      setWizardState(prev => ({
        ...savedState,
        availableForms: savedState.availableForms || prev.availableForms || studyForms,
      }));
    } else {
      // Reset to initial state if no saved state exists for this study
      // Preserve availableForms from current state or studyForms
      setWizardState(prev => ({
        autoMappingEnabled: true,
        selectedFiles: [],
        fileToFormMappings: [],
        columnMappings: [],
        subformRelationships: [],
        validationResults: [],
        isValid: false,
        importMappingId: undefined,
        availableForms: prev.availableForms || studyForms,
      }));
    }
  }, [studyId, studyForms]);

  // Update wizard state with loaded study forms
  useEffect(() => {
    if (studyForms.length > 0) {
      setWizardState(prev => ({
        ...prev,
        availableForms: studyForms,
      }));
    }
  }, [studyForms]);

  // Save mapping objects immediately when they change (with small debounce)
  useEffect(() => {
    if (!studyId) return;

    // Save immediately for critical mapping fields with minimal debounce
    const timeoutId = setTimeout(() => {
      saveMappingState(studyId, wizardStateRef.current);
    }, 50); // Very short debounce for mapping changes (every mapping action)

    return () => clearTimeout(timeoutId);
  }, [
    wizardState.fileToFormMappings,
    wizardState.columnMappings,
    wizardState.subformRelationships,
    studyId,
  ]);

  // Save other state changes with longer debounce
  useEffect(() => {
    if (!studyId) return;

    // Debounce saves to avoid excessive localStorage writes
    const timeoutId = setTimeout(() => {
      saveMappingState(studyId, wizardStateRef.current);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    wizardState.selectedFiles,
    wizardState.autoMappingEnabled,
    wizardState.validationResults,
    wizardState.previewData,
    wizardState.importMappingId,
    wizardState.isValid,
    wizardState.currentPreset,
    studyId,
  ]);

  const updateWizardState = useCallback(
    (updates: Partial<ImportWizardState>) => {
      setWizardState(prev => ({ ...prev, ...updates }));
    },
    []
  );

  const resetWizard = useCallback(() => {
    // Preserve availableForms (study structure) when resetting wizard state
    // This ensures that form mapping remains possible after reset
    setWizardState(prev => ({
      autoMappingEnabled: true, // Reset to enabled
      selectedFiles: [],
      fileToFormMappings: [],
      columnMappings: [],
      subformRelationships: [],
      validationResults: [],
      isValid: false,
      importMappingId: undefined,
      // Preserve study structure (availableForms) so form mapping can still work
      availableForms: prev.availableForms || studyForms,
    }));

    // Clear localStorage for current study
    if (studyId) {
      clearMappingState(studyId);
    }
  }, [studyId, studyForms]);

  // If mappingId is provided, show import progress page
  if (mappingId) {
    return (
      <Container pt="2" pb="2" size="expand" className="study-data-import-page">
        <ImportProgressPage mappingId={mappingId} />
      </Container>
    );
  }

  // If no step is provided (index route), redirect to file-upload
  if (!currentStep) {
    return <Navigate to="/data-import/file-upload" replace />;
  }

  // Validate step parameter
  const validSteps: WizardStep[] = [
    'file-upload',
    'form-detection',
    'preview-and-validation',
    'import',
  ];

  if (!validSteps.includes(currentStep)) {
    return <Navigate to="/data-import/file-upload" replace />;
  }

  // Show loading state while forms are being loaded
  if (loadingForms) {
    return (
      <Container pt="2" pb="2" size="expand" className="study-data-import-page">
        <EmptyContent
          title="Loading study structure..."
          icon={<Spinner size="2" />}
          description="Please wait while we load the study forms structure."
        />
      </Container>
    );
  }

  // Show error if forms failed to load
  if (formsError) {
    return (
      <Container pt="2" pb="2" size="expand" className="study-data-import-page">
        <EmptyContent
          state="error"
          withIconBox
          icon={<IconAlertTriangle />}
          title="Failed to load study structure"
          description={formsError.message || 'Unable to load study forms. Please try again or contact support.'}
        />
      </Container>
    );
  }

  // Check if study structure (forms) exists - required for data import
  if (!loadingForms && !hasForms && studyId) {
    return (
      <Container pt="2" pb="2" size="expand" className="study-data-import-page">
        <Alert color="warning" title="Study structure not found">
          <p>
            This study does not have any forms configured. Data import requires
            study forms to be set up first.
          </p>
          <p style={{ marginTop: 'var(--space-2)' }}>
            Please configure study forms before proceeding with data import.
          </p>
        </Alert>
      </Container>
    );
  }

  return (
    <Container pt="2" pb="2" size="expand" className="study-data-import-page">
      <StudyDataImportWizard
        initialStep={currentStep}
        wizardState={wizardState}
        onUpdateWizardState={updateWizardState}
        onResetWizard={resetWizard}
        loadingForms={loadingForms}
      />
    </Container>
  );
};

export const StudyDataImportPage = withPermissions(StudyDataImport, {
  requiredPermissions: [PERMISSIONS.DATA_ACCESS],
  showRoleSwitcher: true,
});
